export const generateContentMapFragment = (
    contentFields: string,
    contentMapDataFieldFragments: string,
    allDataFieldFragments: string,
    allLocalizedDataFieldFragments: string,
    contentMapLocalizedDataFieldFragments: string
): string => {
    return `... on ContentMap {                        
            ${contentFields ?? ""}
            ${contentMapDataFieldFragments ? `useContext {${contentMapDataFieldFragments}}` : ""}
            ${allDataFieldFragments ? `useContext {${allDataFieldFragments}}` : ""}
            teasers {
                title(length: 256) 
                ${allLocalizedDataFieldFragments} 
                ${contentMapLocalizedDataFieldFragments} 
            }
        }`;
};

export const generateTopicFragment = (
    topicFields: string,
    topicDataFieldFragments: string,
    allDataFieldFragments: string,
    allLocalizedDataFieldFragments: string,
    topicLocalizedDataFieldFragments: string
): string => {
    return ` ... on Topic {            
            ${topicFields ?? ""}
            ${topicDataFieldFragments ? `useContext {${topicDataFieldFragments}}` : ""}
            ${allDataFieldFragments ? `useContext {${allDataFieldFragments}}` : ""}
            teasers {
                title(length: 256) 
                ${allLocalizedDataFieldFragments} 
                ${topicLocalizedDataFieldFragments} 
            }
            mimeType
        }`;
};

export interface ContentNodeFragments {
    contentMapFragment: string;
    topicFragment: string;
    acceptedLanguageParam: string;
}

/**
 * Derives the dynamic `... on ContentMap` / `... on Topic` node fragments and the `$acceptedLanguages`
 * query param (only added when a localized datafield is actually selected) from the display config's
 * datafields/localization fragments and node fields. Shared by all queries that embed a display-config-driven
 * content node selection (e.g. document search, contents grouped).
 */
export const generateContentNodeFragments = (
    dataFieldsFragment: Record<string, string[]>,
    localizationDatafieldsFragment: Record<string, string[]>,
    fields: Record<string, string>
): ContentNodeFragments => {
    const allDataFieldFragments = dataFieldsFragment["all"]?.join(" ") ?? "";
    const allLocalizedDataFieldFragments = localizationDatafieldsFragment["all"]?.join(" ") ?? "";
    const topicDataFieldFragments = dataFieldsFragment["topic"]?.join(" ") ?? "";
    const topicLocalizedDataFieldFragments = localizationDatafieldsFragment["topic"]?.join(" ") ?? "";
    const contentMapDataFieldFragments = dataFieldsFragment["contentMap"]?.join(" ") ?? "";
    const contentMapLocalizedDataFieldFragments = localizationDatafieldsFragment["contentMap"]?.join(" ") ?? "";

    const { topic: topicFields, contentMap: contentFields } = fields;

    const contentMapFragment = generateContentMapFragment(
        contentFields ?? "",
        contentMapDataFieldFragments,
        allDataFieldFragments,
        allLocalizedDataFieldFragments,
        contentMapLocalizedDataFieldFragments
    );

    const topicFragment = generateTopicFragment(
        topicFields ?? "",
        topicDataFieldFragments,
        allDataFieldFragments,
        allLocalizedDataFieldFragments,
        topicLocalizedDataFieldFragments
    );

    const acceptedLanguageParam = (
        allLocalizedDataFieldFragments +
        contentMapLocalizedDataFieldFragments +
        topicLocalizedDataFieldFragments
    ).includes("acceptedLanguages")
        ? ", $acceptedLanguages: [LanguageWithWildcard!]"
        : "";

    return { contentMapFragment, topicFragment, acceptedLanguageParam };
};
